// WAN behaviours (Domain 04): serial point-to-point links and tunnel interfaces
// (GRE and IPsec virtual tunnel interfaces).
//
// Behavioural model, like the rest of the simulator:
//   * Serial: line state follows the cable (L1). The line protocol additionally
//     needs matching Layer 2 encapsulation on both ends (HDLC/PPP) and working
//     keepalives — a carrier fault that keeps the electrical line up but drops
//     frames is modelled as `cable.carrierDown` (status up, protocol down), which
//     is what keepalives detect on a real router.
//   * Tunnels: a tunnel interface is "up" when it has a source and destination;
//     the line protocol comes up when the destination is reachable through the
//     underlay (GRE) — and, for `tunnel mode ipsec ipv4`, when IKE phase 1
//     (matching policy + pre-shared keys for each other's address) and IPsec
//     phase 2 (tunnel protection profile + compatible transform set) succeed.
//
// NO CRYPTOGRAPHY IS PERFORMED. The simulator models the negotiation rules,
// the resulting SA state and packet counters only — which is what an engineer
// verifies and troubleshoots with show commands. Traffic "inside" the tunnel is
// carried by the simulator exactly like any other packet.

import { clone } from './model.js';
import { signature } from './routing.js';
import { forward, routeLookup, l3Addr, ownsIp, linkOf, peerOf, linkState } from './sim.js';
import { isIp } from './addr.js';

export const isSerial = (n) => /^Serial\d/.test(n || '');
export const isTunnel = (n) => /^Tunnel\d+$/.test(n || '');

// ------------------------------------------------------------------ serial
export const serialEncap = (i) => i?.wanEncap || 'hdlc';
/**
 * Line protocol of a serial interface whose cable is connected and both ends are
 * administratively up. Returns null when nothing serial-specific applies.
 */
export function serialStatus(net, id, ifc) {
  const l = linkOf(net, id, ifc);
  if (!l) return null;
  const me = net.devices[id].interfaces[ifc];
  const p = peerOf(l, id, ifc);
  const pd = net.devices[p.dev];
  const pi = pd?.interfaces?.[p.ifc];
  if (l.cable.carrierDown) return { status: 'up', protocol: 'down', connected: true, reason: 'keepalive' };
  if (pi && isSerial(p.ifc) && serialEncap(me) !== serialEncap(pi)) return { status: 'up', protocol: 'down', connected: true, reason: 'encap' };
  return null;
}
/** Which end of a serial cable is the DCE (provides clocking). */
export const isDce = (net, id, ifc) => { const l = linkOf(net, id, ifc); return !!l && l.cable.dce === id; };

// ------------------------------------------------------------------ crypto config helpers
const cryptoOf = (d) => d.crypto || null;
export function tunnelSourceIp(net, id, t) {
  if (!t?.source) return null;
  if (isIp(t.source)) return ownsIp(net, id, t.source) ? t.source : null;
  return l3Addr(net, id, t.source)?.ip || null;
}
const policySig = (p) => `${p.encr || 'des'}|${p.hash || 'sha'}|${p.auth || 'rsa-sig'}|${p.group || 1}`;
function ikePolicyMatch(a, b) {
  const pa = Object.values(cryptoOf(a)?.isakmp?.policies || {}).map(policySig);
  const pb = Object.values(cryptoOf(b)?.isakmp?.policies || {}).map(policySig);
  return pa.find((x) => pb.includes(x) && x.split('|')[2] === 'pre-share') || null;
}
const pskFor = (d, addr) => (cryptoOf(d)?.isakmp?.keys || []).find((k) => k.address === addr || k.address === '0.0.0.0')?.key ?? null;
function transformsOf(d, profileName) {
  const prof = cryptoOf(d)?.profiles?.[profileName];
  if (!prof?.transformSet) return null;
  return cryptoOf(d)?.transformSets?.[prof.transformSet]?.transforms || null;
}

// ------------------------------------------------------------------ tunnel state
const memo = new WeakMap();
const tunnelsOf = (net) => {
  const out = [];
  for (const [id, d] of Object.entries(net.devices)) {
    if (d.kind !== 'router') continue;
    for (const [n, i] of Object.entries(d.interfaces || {})) if (isTunnel(n)) out.push({ id, ifc: n, i });
  }
  return out;
};

/**
 * State of every tunnel interface:
 *   { 'R2|Tunnel0': { status, protocol, src, dst, mode, ike, ipsec, peer:{dev,ifc}, reason } }
 * ike: null (nothing sent) | 'MM_NO_STATE' | 'MM_KEY_EXCH' | 'QM_IDLE'
 */
export function tunnelState(net) {
  const list = tunnelsOf(net);
  if (!list.length) return {};
  const sig = signature(net);
  const m = memo.get(net);
  if (m && m.sig === sig) return m.res;
  // The underlay is evaluated with every tunnel down, so a tunnel can never be
  // carried over itself (IOS: %TUN-5-RECURDOWN).
  const base = clone(net);
  for (const t of list) base.devices[t.id].interfaces[t.ifc].shutdown = true;
  const info = {};
  for (const t of list) {
    const k = `${t.id}|${t.ifc}`;
    const cfg = t.i.tunnel || {};
    const r = { status: 'up', protocol: 'down', mode: cfg.mode || 'gre', src: null, dst: cfg.destination || null, ike: null, ipsec: false, peer: null, reason: null };
    info[k] = r;
    if (t.i.shutdown) { Object.assign(r, { status: 'administratively down', reason: 'shutdown' }); continue; }
    r.src = tunnelSourceIp(base, t.id, cfg);
    if (!cfg.source || !r.src) { r.reason = 'source'; continue; }
    if (!r.dst) { r.reason = 'destination'; continue; }
    const rl = routeLookup(base, t.id, r.dst);
    if (!rl || rl.ifc === 'Null0') { r.reason = 'no-route'; continue; }
    // the far end: a router that owns the destination address and has a tunnel back to us
    const fwd = forward(clone(base), t.id, { src: r.src, dst: r.dst, proto: 'udp', dport: r.mode === 'ipsec' ? 500 : 0, flow: 9e6 });
    const owner = fwd.ok ? fwd.at : null;
    const peerT = owner ? list.find((x) => x.id === owner && !x.i.shutdown && (x.i.tunnel?.destination === r.src) && tunnelSourceIp(base, owner, x.i.tunnel) === r.dst) : null;
    if (r.mode === 'gre') {
      // GRE has no handshake: the line protocol is up once the destination is routable.
      r.protocol = 'up';
      if (peerT && (peerT.i.tunnel?.mode || 'gre') === 'gre') r.peer = { dev: owner, ifc: peerT.ifc };
      continue;
    }
    // ---- IPsec VTI: IKE phase 1, then phase 2
    r.ike = 'MM_NO_STATE';            // we sent IKE packets
    if (!owner) { r.reason = 'peer-unreachable'; continue; }
    if (!peerT || (peerT.i.tunnel?.mode || 'gre') !== 'ipsec') { r.reason = 'peer-no-tunnel'; continue; }
    const back = forward(clone(base), owner, { src: r.dst, dst: r.src, proto: 'udp', dport: 500, flow: 9e6 + 1 });
    if (!back.ok) { r.reason = 'peer-unreachable'; continue; }
    const me = net.devices[t.id]; const pd = net.devices[owner];
    if (!ikePolicyMatch(me, pd)) { r.reason = 'policy'; continue; }
    const kMine = pskFor(me, r.dst); const kPeer = pskFor(pd, r.src);
    if (kMine == null || kPeer == null) { r.reason = kMine == null ? 'no-key' : 'peer-no-key'; continue; }
    if (kMine !== kPeer) { r.ike = 'MM_KEY_EXCH'; r.reason = 'key-mismatch'; continue; }
    r.ike = 'QM_IDLE';
    const ta = transformsOf(me, cfg.protection); const tb = transformsOf(pd, peerT.i.tunnel?.protection);
    if (!ta || !tb) { r.reason = !ta ? 'no-protection' : 'peer-no-protection'; continue; }
    if (ta.slice().sort().join() !== tb.slice().sort().join()) { r.reason = 'transform'; continue; }
    Object.assign(r, { ipsec: true, protocol: 'up', peer: { dev: owner, ifc: peerT.ifc } });
  }
  // A tunnel only carries traffic when BOTH ends consider it up.
  for (const [k, r] of Object.entries(info)) {
    if (!r.peer) continue;
    const o = info[`${r.peer.dev}|${r.peer.ifc}`];
    if (!o || o.protocol !== 'up' || (r.mode === 'ipsec' && !o.ipsec)) { r.peer = null; if (r.mode === 'ipsec') { r.protocol = 'down'; r.ipsec = false; r.reason = r.reason || 'peer-down'; } }
  }
  memo.set(net, { sig, res: info });
  return info;
}

/** ifStatus() for Tunnel interfaces (called by sim.ifStatus). */
export function tunnelIfStatus(net, id, ifc) {
  const r = tunnelState(net)[`${id}|${ifc}`];
  if (!r) return { status: 'down', protocol: 'down', connected: false };
  return { status: r.status, protocol: r.protocol, connected: r.protocol === 'up', tunnel: r };
}

/** The far tunnel endpoint (used by l2Flood): point-to-point, no ARP. */
export function tunnelPeers(net, id, ifc) {
  const r = tunnelState(net)[`${id}|${ifc}`];
  if (!r || r.protocol !== 'up' || !r.peer) return [];
  return [{ dev: r.peer.dev, ifc: r.peer.ifc, hops: [], errLinks: [] }];
}

/** Packet counters on tunnels (show crypto ipsec sa: #pkts encaps / decaps). */
export function countTunnel(net, id, ifc, peer) {
  const i = net.devices[id]?.interfaces?.[ifc];
  if (i?.counters) i.counters.outPkts++;
  const pi = peer && net.devices[peer.dev]?.interfaces?.[peer.ifc];
  if (pi?.counters) pi.counters.inPkts++;
}

/** Serial interfaces never ARP: any route out of them reaches the single peer. */
export const isPointToPoint = (ifc) => isSerial(ifc) || isTunnel(ifc);
export { linkState };
